import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Form, Button, Alert, Spinner, Modal, Table, Badge } from 'react-bootstrap';

import coldemailer_api from './coldemailer_api';
import coldemailer_config from './coldemailer_config';
import CreateCampaignEmailsModal from './modals/CreateCampaignEmailsModal';
import {
	c_PeriodTypeOptions,
	to_date_input_value,
	calculate_end_date
} from './cadence_options';
import './coldemailer.css';
import './coldemailer-styles.css';

const c_StatusOptions = [
	{ value: 'inactive', label: 'Inactive' },
	{ value: 'active', label: 'Active' },
	{ value: 'paused', label: 'Paused' },
	{ value: 'archived', label: 'Archived' }
];

function EmailCampaignView() {
	const navigate = useNavigate();
	const { id } = useParams();

	const [campaign, set_campaign] = useState(null);
	const [email_senders, set_email_senders] = useState([]);
	const [form_values, set_form_values] = useState(null);
	const [loading, set_loading] = useState(true);
	const [saving, set_saving] = useState(false);
	const [error, set_error] = useState('');
	const [saved_message, set_saved_message] = useState('');
	const [show_archive_modal, set_show_archive_modal] = useState(false);
	const [archiving, set_archiving] = useState(false);
	const [campaign_emails, set_campaign_emails] = useState([]);
	const [campaign_emails_loading, set_campaign_emails_loading] = useState(true);
	const [campaign_emails_error, set_campaign_emails_error] = useState('');
	const [show_create_emails_modal, set_show_create_emails_modal] = useState(false);

	const build_form_values = (loaded_campaign) => {
		return {
			name: loaded_campaign.name ? loaded_campaign.name : '',
			description: loaded_campaign.description ? loaded_campaign.description : '',
			email_sender_id: loaded_campaign.email_sender_id ? loaded_campaign.email_sender_id : '',
			landing_page_base_url: loaded_campaign.landing_page_base_url ? loaded_campaign.landing_page_base_url : '',
			unsubscribe_base_url: loaded_campaign.unsubscribe_base_url ? loaded_campaign.unsubscribe_base_url : '',
			status: loaded_campaign.status ? loaded_campaign.status : 'inactive',
			// No fallback: the create modal always sends a cadence, so a null one is a data problem
			// worth seeing rather than papering over with a default that misrepresents what is stored
			period: loaded_campaign.period ? String(loaded_campaign.period) : '',
			period_type: loaded_campaign.period_type ? loaded_campaign.period_type : '',
			start_date: to_date_input_value(loaded_campaign.start_date)
		};
	};

	const load_campaign = async () => {
		set_loading(true);
		set_error('');

		try {
			const loaded_campaign = await coldemailer_api.get_email_campaign(id);
			const senders = await coldemailer_api.get_email_senders();

			set_campaign(loaded_campaign);
			set_form_values(build_form_values(loaded_campaign));
			set_email_senders(senders);
		} catch (error) {
			console.log('EmailCampaignView.load_campaign - ERROR: ', error);
			set_error('Failed to load the campaign.');
		} finally {
			set_loading(false);
		}
	};

	const load_campaign_emails = async () => {
		set_campaign_emails_loading(true);
		set_campaign_emails_error('');

		try {
			const emails = await coldemailer_api.get_campaign_emails(id);
			set_campaign_emails(emails);
		} catch (error) {
			console.log('EmailCampaignView.load_campaign_emails - ERROR: ', error);
			set_campaign_emails_error('Failed to load campaign emails.');
		} finally {
			set_campaign_emails_loading(false);
		}
	};

	useEffect(function() {
		load_campaign();
		load_campaign_emails();
	}, [id]);

	const on_field_change = (field_name) => {
		return (event) => {
			const value = event.target.value;
			set_saved_message('');
			set_form_values(function(previous) {
				return Object.assign({}, previous, { [field_name]: value });
			});
		};
	};

	const has_changes = () => {
		if(!campaign || !form_values) {
			return false;
		}

		const original = build_form_values(campaign);
		const names = Object.keys(original);

		for(var i=0; i < names.length; ++i) {
			if(original[names[i]] !== form_values[names[i]]) {
				return true;
			}
		}

		return false;
	};

	const can_save = () => {
		if(!form_values) {
			return false;
		}

		if(!form_values.name.trim()) {
			return false;
		}

		if(!form_values.email_sender_id) {
			return false;
		}

		if(!form_values.landing_page_base_url.trim()) {
			return false;
		}

		const period = parseInt(form_values.period, 10);
		if(!period || period < 1) {
			return false;
		}

		if(!form_values.period_type) {
			return false;
		}

		return has_changes();
	};

	const handle_save = async () => {
		set_saving(true);
		set_error('');
		set_saved_message('');

		try {
			// total_emails is not written: the sending path counts CampaignEmail rows, so a stored
			// copy would be a cache with no reader that could only go stale
			const end_date = calculate_end_date(form_values.start_date, form_values.period, form_values.period_type, campaign_emails.length);

			const updated = await coldemailer_api.update_email_campaign(id, {
				name: form_values.name.trim(),
				description: form_values.description.trim(),
				email_sender_id: form_values.email_sender_id,
				landing_page_base_url: form_values.landing_page_base_url.trim(),
				unsubscribe_base_url: form_values.unsubscribe_base_url.trim(),
				status: form_values.status,
				period: parseInt(form_values.period, 10),
				period_type: form_values.period_type,
				start_date: form_values.start_date ? form_values.start_date : null,
				end_date: end_date ? end_date : null
			});

			set_campaign(updated);
			set_form_values(build_form_values(updated));
			set_saved_message('Campaign saved.');
		} catch (error) {
			console.log('EmailCampaignView.handle_save - ERROR: ', error);

			const message = error.response && error.response.data && error.response.data.message ? error.response.data.message : 'Failed to save the campaign.';
			set_error(message);
		} finally {
			set_saving(false);
		}
	};

	const handle_archive = async () => {
		set_archiving(true);
		set_error('');

		try {
			await coldemailer_api.archive_email_campaign(id);
			navigate(coldemailer_config.campaign_route);
		} catch (error) {
			console.log('EmailCampaignView.handle_archive - ERROR: ', error);
			set_error('Failed to archive the campaign.');
			set_archiving(false);
			set_show_archive_modal(false);
		}
	};

	const handle_back = () => {
		navigate(coldemailer_config.campaign_route);
	};

	const render_header = () => {
		return (
			<div className="ce-header">
				<div>
					<h4 className="ce-title">{campaign && campaign.name ? campaign.name : 'Email Campaign'}</h4>
					<p className="ce-subtitle">Edit this campaign's details</p>
				</div>

				<div className="ce-header-actions">
					<Button variant="outline-light" size="sm" className="ce-button" onClick={handle_back}>
						Back
					</Button>

					<Button
						variant="outline-danger"
						size="sm"
						onClick={function() { set_show_archive_modal(true); }}
						disabled={saving || (campaign && campaign.status === 'archived')}
					>
						Archive
					</Button>

					<Button
						size="sm"
						className="ce-button-create"
						onClick={handle_save}
						disabled={!can_save() || saving}
					>
						{saving ? 'Saving...' : 'Save'}
					</Button>
				</div>
			</div>
		);
	};

	const render_loading = () => {
		return (
			<div className="ce-loading">
				<Spinner animation="border" size="sm" />
			</div>
		);
	};

	// Derived from the campaign's own emails, so it cannot disagree with what will actually be sent
	const render_end_date = () => {
		if(!campaign_emails.length) {
			return 'No emails yet';
		}

		const end_date = calculate_end_date(form_values.start_date, form_values.period, form_values.period_type, campaign_emails.length);
		if(!end_date) {
			return 'Set a start date';
		}

		if(form_values.period_type === 'days') {
			return end_date.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
		}

		return end_date.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' }) +
				' ' +
				end_date.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
	};

	const render_form = () => {
		return (
			<div className="ce-panel">
				<Form>
					<Form.Group className="ce-modal-field">
						<Form.Label className="ce-modal-label">Name</Form.Label>
						<Form.Control
							type="text"
							value={form_values.name}
							onChange={on_field_change('name')}
							disabled={saving}
						/>
					</Form.Group>

					<Form.Group className="ce-modal-field">
						<Form.Label className="ce-modal-label">Description</Form.Label>
						<Form.Control
							as="textarea"
							rows={2}
							value={form_values.description}
							onChange={on_field_change('description')}
							disabled={saving}
						/>
					</Form.Group>

					<Form.Group className="ce-modal-field">
						<Form.Label className="ce-modal-label">Sender</Form.Label>
						<Form.Select
							value={form_values.email_sender_id}
							onChange={on_field_change('email_sender_id')}
							disabled={saving}
						>
							<option value="">Select a sender</option>
							{email_senders.map(function(sender) {
								return (
									<option key={sender.id} value={sender.id}>
										{sender.company_name ? sender.company_name : sender.name}
									</option>
								);
							})}
						</Form.Select>
						<Form.Text className="ce-modal-help">
							Identity shown in the compliance footer of every email.
						</Form.Text>
					</Form.Group>

					<Form.Group className="ce-modal-field">
						<Form.Label className="ce-modal-label">Landing Page Base URL</Form.Label>
						<Form.Control
							type="text"
							value={form_values.landing_page_base_url}
							onChange={on_field_change('landing_page_base_url')}
							disabled={saving}
						/>
						<Form.Text className="ce-modal-help">
							Each recipient's link becomes this URL plus their unique id.
						</Form.Text>
					</Form.Group>

					<Form.Group className="ce-modal-field">
						<Form.Label className="ce-modal-label">Unsubscribe Base URL</Form.Label>
						<Form.Control
							type="text"
							value={form_values.unsubscribe_base_url}
							onChange={on_field_change('unsubscribe_base_url')}
							disabled={saving}
						/>
						<Form.Text className="ce-modal-help">
							Host only, with no path: the opt-out path is added automatically. A send is refused without it.
						</Form.Text>
					</Form.Group>

					<Form.Group className="ce-modal-field">
						<Form.Label className="ce-modal-label">Send Cadence</Form.Label>

						<div className="ce-form-row">
							<Form.Group className="ce-field-period">
								<Form.Control
									type="number"
									min={1}
									step={1}
									value={form_values.period}
									onChange={on_field_change('period')}
									disabled={saving}
								/>
							</Form.Group>

							<Form.Group className="ce-field-period-type">
								<Form.Select
									value={form_values.period_type}
									onChange={on_field_change('period_type')}
									disabled={saving}
								>
									{form_values.period_type ? null : <option value="">Not set</option>}
									{c_PeriodTypeOptions.map(function(option) {
										return (
											<option key={option.value} value={option.value}>
												{option.label}
											</option>
										);
									})}
								</Form.Select>
							</Form.Group>
						</div>

						<Form.Text className="ce-modal-help">
							How long to wait before the next email in the sequence.
						</Form.Text>
					</Form.Group>

					<div className="ce-form-row ce-form-row-wrap">
						<Form.Group className="ce-modal-field ce-field-date">
							<Form.Label className="ce-modal-label">Start Date</Form.Label>
							<Form.Control
								type="date"
								value={form_values.start_date}
								onChange={on_field_change('start_date')}
								disabled={saving}
							/>
						</Form.Group>

						<Form.Group className="ce-modal-field ce-field-date">
							<Form.Label className="ce-modal-label">End Date</Form.Label>
							<Form.Control
								type="text"
								value={render_end_date()}
								readOnly
								disabled
							/>
						</Form.Group>

						<Form.Text className="ce-modal-help ce-end-date-help">
							End date is worked out from the start date, the cadence and the campaign's emails below.
						</Form.Text>
					</div>

					<Form.Group className="ce-modal-field">
						<Form.Label className="ce-modal-label">Status</Form.Label>
						<Form.Select
							value={form_values.status}
							onChange={on_field_change('status')}
							disabled={saving}
						>
							{c_StatusOptions.map(function(option) {
								return (
									<option key={option.value} value={option.value}>
										{option.label}
									</option>
								);
							})}
						</Form.Select>
					</Form.Group>
				</Form>
			</div>
		);
	};

	const render_campaign_emails_empty = () => {
		return (
			<tr>
				<td colSpan={5} className="ce-empty">
					No campaign emails yet.
				</td>
			</tr>
		);
	};

	const handle_campaign_email_click = (campaign_email_id) => {
		return () => {
			navigate(coldemailer_config.campaign_email_route + '/' + campaign_email_id);
		};
	};

	const render_campaign_email_row = (campaign_email) => {
		return (
			<tr
				key={campaign_email.id}
				className="ce-row-clickable"
				onClick={handle_campaign_email_click(campaign_email.id)}
			>
				<td className="ce-ce-col-touch">
					{campaign_email.touch_number ? campaign_email.touch_number : '-'}
				</td>
				<td className="ce-ce-col-name">
					{campaign_email.name ? campaign_email.name : '-'}
				</td>
				<td className="ce-ce-col-subject">
					{campaign_email.subject ? campaign_email.subject : '-'}
				</td>
				<td className="ce-ce-col-type">
					<Badge bg={campaign_email.content_type === 'html' ? 'info' : 'secondary'}>
						{campaign_email.content_type ? campaign_email.content_type : 'text'}
					</Badge>
				</td>
				<td className="ce-ce-col-status">
					{campaign_email.status ? campaign_email.status : '-'}
				</td>
			</tr>
		);
	};

	const render_campaign_emails = () => {
		return (
			<div className="ce-panel ce-emails-panel">
				<div className="ce-panel-header">
					<h5 className="ce-panel-title">Campaign Emails</h5>

					<div className="ce-header-actions">
						<Button variant="outline-light" size="sm" className="ce-button" onClick={load_campaign_emails}>
							Refresh
						</Button>

						<Button
							size="sm"
							className="ce-button-create"
							onClick={function() { set_show_create_emails_modal(true); }}
						>
							Create
						</Button>
					</div>
				</div>

				{campaign_emails_error ? <Alert variant="danger">{campaign_emails_error}</Alert> : null}

				{campaign_emails_loading ? (
					<div className="ce-loading">
						<Spinner animation="border" size="sm" />
					</div>
				) : (
					<Table striped bordered hover responsive variant="dark" className="ce-table">
						<thead>
							<tr>
								<th className="ce-ce-col-touch">Touch</th>
								<th className="ce-ce-col-name">Name</th>
								<th className="ce-ce-col-subject">Subject</th>
								<th className="ce-ce-col-type">Type</th>
								<th className="ce-ce-col-status">Status</th>
							</tr>
						</thead>
						<tbody>
							{campaign_emails.length === 0 ? render_campaign_emails_empty() : campaign_emails.map(render_campaign_email_row)}
						</tbody>
					</Table>
				)}
			</div>
		);
	};

	const render_create_emails_modal = () => {
		return (
			<CreateCampaignEmailsModal
				show={show_create_emails_modal}
				on_hide={function() { set_show_create_emails_modal(false); }}
				email_campaign_id={id}
				on_created={load_campaign_emails}
			/>
		);
	};

	const render_archive_modal = () => {
		return (
			<Modal show={show_archive_modal} onHide={function() { if(!archiving) { set_show_archive_modal(false); } }} centered size="sm">
				<Modal.Header closeButton>
					<Modal.Title>Archive Campaign</Modal.Title>
				</Modal.Header>

				<Modal.Body>
					<p>Archive <strong>{campaign ? campaign.name : ''}</strong>?</p>
					<p>It stays in the database, but is marked archived and no longer active.</p>
				</Modal.Body>

				<Modal.Footer>
					<Button variant="secondary" onClick={function() { set_show_archive_modal(false); }} disabled={archiving}>
						Cancel
					</Button>
					<Button variant="danger" onClick={handle_archive} disabled={archiving}>
						{archiving ? 'Archiving...' : 'Archive'}
					</Button>
				</Modal.Footer>
			</Modal>
		);
	};

	const render_content = () => {
		if(loading) {
			return render_loading();
		}

		if(!campaign || !form_values) {
			return null;
		}

		return (
			<React.Fragment>
				{render_form()}
				{render_campaign_emails()}
				{render_create_emails_modal()}
				{render_archive_modal()}
			</React.Fragment>
		);
	};

	const root_style = { '--ce-top-offset': coldemailer_config.top_offset_px + 'px' };

	return (
		<div className="ce-root" style={root_style}>
			{render_header()}

			{error ? <Alert variant="danger">{error}</Alert> : null}
			{saved_message ? <Alert variant="success">{saved_message}</Alert> : null}

			{render_content()}
		</div>
	);
}

export default EmailCampaignView;
